import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { catchError, Subscription, switchMap } from 'rxjs';
import { PostalAddress } from '../../../common/model/postal-address';
import { MultiselectListViewComponent } from '../../../core/component/list/multiselect-list-view/multiselect-list-view.component';
import { ColumnDefinition } from '../../../core/model/column-definition';
import { ComponentMessageDispatcher } from '../../../core/service/component-message-dispather.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { InvoiceDeliveryMethod } from '../../../invoice/model/const/enum';
import { InvoiceService } from '../../../invoice/service/data/invoice.service';
import { DocumentTypeMimeTypes } from '../../../core/model/const/document-type-mime-types';
import { LocalDate } from '../../../core/model/local-date';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { MonthYearItem } from '../../../core/model/period';
import { getMonthName } from '../../../core/helper/month';
import { RentInvoicingOrderDetailsService } from '../../service/data/rent-invoicing/rent-invoicing-order-details.service';
import { RentInvoicingOrderCreateSingleModel } from '../../model/rent-invoicing/rent-invoicing-order-create-single-model';
import { RentInvoicingOrderSettings } from '../../model/rent-invoicing/rent-invoicing-order-settings';
import { RentInvoicingOrderService } from '../../service/data/rent-invoicing/rent-invoicing-order.service';
import { RentInvoicingOrderSingleLeaseService } from '../../service/data/rent-invoicing/rent-invoicing-order-single-lease-service';

@Component({
    selector: 'app-send-invoice',
    templateUrl: './create-lease-agreement-invoices.component.html',
    styleUrls: ['./create-lease-agreement-invoices.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreateLeaseAgreementInvoicesComponent implements OnInit, OnDestroy {
  constructor(
    private _dataService: RentInvoicingOrderSingleLeaseService,
    private _factory: ColumnDefinitionFactory,
    dispatcher: ComponentMessageDispatcher,
    private _fb: FormBuilder,
    private _messageService: MessageService,
    private _navigationService: NavigationService
  ) {
    this.form = this._fb.group({});
    this.appendixes = this._fb.array([]);

    this._subscription = dispatcher.messages.subscribe(message => {
      this._leaseAgreement = message;
      dispatcher.messageDelivered();
    });
  }

  @ViewChild('contractList') contractList: MultiselectListViewComponent | null = null;
  @ViewChild('rentPeriodList') rentPeriodList: MultiselectListViewComponent | null = null;

  private _leaseAgreement: string | null = null;
  private _subscription: Subscription | null;
  contracts: any[] = [];
  rentPeriods: any[] = [];
  relevantRentPeriods: any[] = [];
  invoiceDeliveryMethod: InvoiceDeliveryMethod = 1;
  contractColumnDefinition: ColumnDefinition[] = [];
  rentPeriodColumnDefinition: ColumnDefinition[] = [];

  protected disableLoadEarlierPeriods: boolean = false;
  protected disableLoadLaterPeriods: boolean = false;

  get hasJointDebiting(): boolean {
    return this.contracts && this.contracts.length > 0;
  }

  get displayInvoiceAddress(): boolean {
    return (
      this.invoiceDeliveryMethod &&
      (this.invoiceDeliveryMethod === InvoiceDeliveryMethod.Mail || this.invoiceDeliveryMethod === InvoiceDeliveryMethod.Manual)
    );
  }

  invoiceAddress: PostalAddress | null = null;
  get invoiceAddressForm(): AbstractControl {
    return this.form.controls['invoiceAddress'];
  }

  private _getColumnDefinitionGroup() {
    let columns = [
      this._factory.create('JOINT_DEBITING_LIST.LEASE_AGREEMENT_NUMBER_COLUMN', 'leaseAgreementNumber'),
      this._factory.create('JOINT_DEBITING_LIST.RENTAL_OBJECT_ADDRESS_COLUMN', 'rentalObjectAddress'),
      this._factory.create('JOINT_DEBITING_LIST.RENTAL_OBJECT_TYPE_COLUMN', 'rentalObjectType'),
      this._factory.createDate('JOINT_DEBITING_LIST.FROM_DATE_COLUMN', 'fromDate'),
      this._factory.createDate('JOINT_DEBITING_LIST.TO_DATE_COLUMN', 'lastBillingDate'),
    ];
    return columns;
  }

  private _getRentPeriodColumnDefinitionGroup() {
    let columns = [this._factory.create('SEND_INVOICES_FORM.PERIOD_LABEL', 'periodText')];
    return columns;
  }

  appendixes: FormArray;
  mimeTypes: string[] = DocumentTypeMimeTypes.PDF();

  create() {
    let model = new RentInvoicingOrderCreateSingleModel();
    model.appendixes = this.appendixes.controls.map(f => f.value.data);
    model.settings = new RentInvoicingOrderSettings();
    model.settings.rentPeriodIds = this.previewedRentPeriodIds;
    model.settings.leaseAgreementIds = this.previewedLeaseAgreementIds;
    model.settings.recipientAddress = this.form.controls['invoiceAddress'].value;
    this._dataService
      .createNewSingleLease(model)
      .pipe(
        catchError(e => {
          this._messageService.showError(e);
          throw e;
        })
      )
      .subscribe(val => {
        this._navigationService.navigate(['rent-invoicing', 'edit', val.id]);
      });
  }

  ngOnDestroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription = null;
    }
  }

  private setRelevantRentPeriods() {
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 2);
    const threeMonthsAgoInUNIX = Math.floor(threeMonthsAgo.getTime() / 1000);

    const filteredRentPeriods = this.rentPeriods.filter(period => {
      const dueDateInUNIX = Math.floor(new Date(LocalDate.fromDate(period.dueDate).toISOString()).getTime() / 1000);
      return dueDateInUNIX > threeMonthsAgoInUNIX;
    });

    this.relevantRentPeriods = filteredRentPeriods.slice(0, 6);
  }

  protected loadEarlierPeriods() {
    const indexOfFirstRelevant = this.rentPeriods.indexOf(this.relevantRentPeriods[0]);

    if (indexOfFirstRelevant !== 0) {
      const sliceStartValue = indexOfFirstRelevant - 10 < 0 ? 0 : indexOfFirstRelevant - 10;
      const tenEarlierPeriods = this.rentPeriods.slice(sliceStartValue, indexOfFirstRelevant);
      this.relevantRentPeriods = [...tenEarlierPeriods, ...this.relevantRentPeriods];

      if (this.rentPeriods.indexOf(this.relevantRentPeriods[0]) === 0) this.disableLoadEarlierPeriods = true;
    }
  }

  protected loadLaterPeriods() {
    const indexOfLastRelevant = this.rentPeriods.indexOf(this.relevantRentPeriods[this.relevantRentPeriods.length - 1]);

    if (indexOfLastRelevant < this.rentPeriods.length) {
      const sliceStartValue = indexOfLastRelevant + 1;
      const sliceEndValue = indexOfLastRelevant + 10 > this.rentPeriods.length ? this.rentPeriods.length : indexOfLastRelevant + 10;
      const tenLaterPeriods = this.rentPeriods.slice(sliceStartValue, sliceEndValue);
      this.relevantRentPeriods = [...this.relevantRentPeriods, ...tenLaterPeriods];

      if (this.rentPeriods.indexOf(this.relevantRentPeriods[this.relevantRentPeriods.length - 1]) === this.rentPeriods.length - 1)
        this.disableLoadLaterPeriods = true;
    }
  }

  ngOnInit() {
    this.contractColumnDefinition = this._getColumnDefinitionGroup();
    this.rentPeriodColumnDefinition = this._getRentPeriodColumnDefinitionGroup();
    if (!this._leaseAgreement) {
      this._navigationService.navigateBack();
      return;
    }

    this._dataService.getLeaseAgreementNewInvoicesForm(this._leaseAgreement).subscribe(
      data => {
        if (data) {
          this.form.addControl('invoiceAddress', this._fb.group({}));
          this.invoiceAddress = data.invoiceAddress ?? new PostalAddress();
          this.contracts = data.jointDebiting;
          this.rentPeriods = data.rentPeriods;
          this.setRelevantRentPeriods();
          this.invoiceDeliveryMethod = data.invoiceDeliveryMethod;
          this.initialize(data);
        }
      },
      error => this._messageService.showError(error)
    );
  }

  previewFrom: { year: number; month: string } | null = null;
  previewTo: { year: number; month: string } | null = null;

  previewedLeaseAgreementIds: string[] = [];
  previewedRentPeriodIds: string[] = [];
  preview() {
    if (this.form.invalid) return;

    let formValue = this.form.value;
    const selectedRentPeriods = this.rentPeriodList ? this.rentPeriodList.getSelectedItems() : [];

    const model = new RentInvoicingOrderSettings();
    model.recipientAddress = formValue.invoiceAddress;
    model.rentPeriodIds = selectedRentPeriods.map(item => item.id);
    model.leaseAgreementIds = this.contractList ? this.contractList.getSelectedItems().map(item => item.id) : [];

    this.previewedLeaseAgreementIds = model.leaseAgreementIds;
    this.previewedRentPeriodIds = model.rentPeriodIds;

    this.form.disable();
    this._dataService
      .prepareImdForPreview(model)
      .pipe(
        switchMap(() => this._dataService.previewSingle(model)),
        catchError(error => {
          this.form.enable();
          this._messageService.showError(error);
          throw error;
        })
      )
      .subscribe(data => {
        this.previewDataContext = data;
        this.form.enable();
        if (selectedRentPeriods.length > 0) {
          this.previewFrom = { year: selectedRentPeriods[0].year, month: selectedRentPeriods[0].month };
          this.previewTo = {
            year: selectedRentPeriods[selectedRentPeriods.length - 1].year,
            month: selectedRentPeriods[selectedRentPeriods.length - 1].month,
          };
        } else {
          this.previewFrom = null;
          this.previewTo = null;
        }
      });
  }

  get canCreate(): boolean {
    return (
      this.previewDataContext &&
      Array.isArray(this.previewDataContext) &&
      this.previewDataContext.length > 0 &&
      Array.isArray(this.previewDataContext[0].invoices) &&
      this.previewDataContext[0].invoices.length > 0
    );
  }
  form: FormGroup;
  initialized: boolean = false;
  previewDataContext: any;
  invoicingEnd: any;
  invoicingStart: any;
  itemsSource: MonthYearItem[] = [];
  moduleActive: boolean = false;
  processing: boolean = false;

  protected initialize(formModel: any) {
    this.processing = formModel.inProgress;
    this.moduleActive = formModel.isInvoicingModuleActive;
    this.initialized = true;
  }

  cancel() {
    this.previewDataContext = null;
  }
}
